import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../logger';

// Real-time field validation
export const validateField = async (field, value, existingData = {}, isSignIn = false) => {
  try {
    if (!value) {
      return {
        isValid: false,
        message: `${field} is required`,
        style: 'border-red-500 focus:ring-red-500'
      };
    }

    switch (field) {
      case 'email':
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return { 
            isValid: false, 
            message: 'Please enter a valid email address',
            style: 'border-red-500 focus:ring-red-500'
          };
        }
        
        // Only check for existing email during signup, not signin
        if (!isSignIn) {
          try {
            const userRef = doc(db, 'userProfiles', value.toLowerCase());
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
              // Check if email is pending verification
              const pendingEmail = localStorage.getItem('pendingEmail');
              if (pendingEmail === value) {
                return {
                  isValid: false,
                  message: 'Email verification required',
                  requiresVerification: true,
                  style: 'border-yellow-500 focus:ring-yellow-500'
                };
              }
              
              return { 
                isValid: false, 
                message: 'This email is already registered',
                style: 'border-red-500 focus:ring-red-500'
              };
            }
          } catch (err) {
            // Handle permission errors gracefully
            if (err.code === 'permission-denied') {
              logInfo('Email existence check skipped - will validate during signup');
              return {
                isValid: true,
                message: 'Email will be validated during signup',
                style: 'border-blue-500 focus:ring-blue-500'
              };
            }
            logError('Email validation error:', err);
            return {
              isValid: false,
              message: 'Unable to validate email. Please try again.',
              style: 'border-red-500 focus:ring-red-500'
            };
          }
        }

        return { 
          isValid: true, 
          message: isSignIn ? '' : 'Email available',
          style: 'border-green-500 focus:ring-green-500'
        };

      case 'password':
        const requirements = [
          { met: value.length >= 8, text: 'At least 8 characters' },
          { met: /[A-Z]/.test(value), text: 'One uppercase letter' },
          { met: /[a-z]/.test(value), text: 'One lowercase letter' },
          { met: /[0-9]/.test(value), text: 'One number' },
          { met: /[!@#$%^&*]/.test(value), text: 'One special character' }
        ];

        const allMet = requirements.every(req => req.met);
        return {
          isValid: allMet,
          message: allMet ? 'Password meets requirements' : 'Password does not meet requirements',
          style: allMet ? 'border-green-500 focus:ring-green-500' : 'border-red-500 focus:ring-red-500',
          requirements
        };

      default:
        return { isValid: true };
    }
  } catch (error) {
    logError('Field validation failed:', error);
    return {
      isValid: false,
      message: 'Validation failed. Please try again.',
      style: 'border-red-500 focus:ring-red-500'
    };
  }
};

// Validate all required fields based on plan type
export const validateSignupForm = async (data, planType) => {
  const errors = {};
  console.log('[SignupValidation] Validating form data:', { planType, data });

  const requiredFields = {
    free: ['email', 'password', 'name', 'phone'],
    paid: ['email', 'password', 'name', 'phone', 'company', 'address'],
    token: ['email', 'password', 'name', 'phone']
  };

  // Validate plan type
  if (!requiredFields[planType]) {
    logError('Invalid plan type:', planType);
    return {
      isValid: false,
      errors: { planType: 'Invalid subscription plan' }
    };
  }

  for (const field of requiredFields[planType]) {
    if (!data[field]) {
      errors[field] = `${field} is required`;
      continue;
    }

    const validation = await validateField(field, data[field], data);
    if (!validation.isValid) {
      errors[field] = validation.message;
    }
  }

  console.log('[SignupValidation] Validation result:', { 
    isValid: Object.keys(errors).length === 0,
    errors
  });
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};